import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import { Logo, ArrowLeftIcon, CheckIcon } from '../UI/Icons';

const highlights = [
  'Upload a transcript, get a term-by-term plan',
  'Track study terms and co-op work terms together',
  'Ask an advisor that knows your academic record',
];

const passwordRules = [
  { label: '8+ characters', test: (v: string) => v.length >= 8 },
  { label: 'Uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { label: 'Lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { label: 'Number', test: (v: string) => /\d/.test(v) },
  { label: 'Special character', test: (v: string) => /[!@#$%^&*(),.?":{}|<>]/.test(v) },
];

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.email.toLowerCase().endsWith('@uwaterloo.ca')) {
      setError('Only @uwaterloo.ca email addresses are allowed');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const failedRule = passwordRules.find((rule) => !rule.test(formData.password));
    if (failedRule) {
      setError(`Password must include: ${failedRule.label.toLowerCase()}`);
      return;
    }

    setLoading(true);

    try {
      await authService.register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
      });
      navigate('/login');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg).join('. '));
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to register. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E9E9EE] p-3 sm:p-6">
      <div className="mx-auto grid min-h-[calc(100vh-1.5rem)] max-w-[1400px] overflow-hidden rounded-3xl bg-white shadow-shell sm:min-h-[calc(100vh-3rem)] lg:grid-cols-2">
        {/* ---- Brand panel ---- */}
        <div className="mesh-gradient relative hidden flex-col justify-between p-10 lg:flex">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={42} />
            <span className="font-display text-xl font-semibold tracking-tight text-canvas-900">
              UW Pathfinder
            </span>
          </Link>

          <div>
            <h2 className="max-w-md font-display text-4xl font-bold leading-[1.1] tracking-[-0.02em] text-canvas-800">
              Start planning in a couple of minutes
            </h2>
            <ul className="mt-8 space-y-3">
              {highlights.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-canvas-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/70 text-primary-700 shadow-pill">
                    <CheckIcon width={13} height={13} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-canvas-600">
            An independent student project · Not affiliated with the University of Waterloo
          </p>
        </div>

        {/* ---- Form panel ---- */}
        <div className="flex items-center justify-center px-5 py-12 sm:px-12">
          <div className="w-full max-w-sm">
            <Link
              to="/"
              className="mb-8 inline-flex items-center gap-1.5 text-sm text-canvas-600 transition-colors hover:text-canvas-900 lg:mb-10"
            >
              <ArrowLeftIcon width={16} height={16} />
              Back to home
            </Link>

            <div className="lg:hidden">
              <Logo size={44} />
            </div>

            <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-canvas-900 lg:mt-0">
              Create your account
            </h1>
            <p className="mt-2 text-sm text-canvas-600">
              Use your @uwaterloo.ca email to get started.
            </p>

            <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="full_name" className="label-field">
                  Full name
                </label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Alex Chen"
                />
              </div>

              <div>
                <label htmlFor="email" className="label-field">
                  UWaterloo email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="you@uwaterloo.ca"
                />
              </div>

              <div>
                <label htmlFor="password" className="label-field">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="••••••••"
                />
                {formData.password && (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {passwordRules.map((rule) => {
                      const passed = rule.test(formData.password);
                      return (
                        <span
                          key={rule.label}
                          className={`chip ${
                            passed ? 'bg-emerald-50 text-emerald-700' : 'bg-canvas-100 text-canvas-500'
                          }`}
                        >
                          {passed && <CheckIcon width={11} height={11} />}
                          {rule.label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="label-field">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="••••••••"
                />
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full py-3">
                {loading ? 'Creating account…' : 'Create account'}
              </button>
            </form>

            <p className="mt-7 text-center text-sm text-canvas-600">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-primary-700 hover:text-primary-800">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
